export const SIPOC_PROCESS_STEP_THRESHOLD = 7;
export const SIPOC_QUALITY_VALID_THRESHOLD = 70;
export const SIPOC_MINIMUMS = {
  suppliers: 3,
  inputs: 3,
  process_steps: 3,
  outputs: 2,
  customers: 2,
};

const ROOT_CAUSE_LANGUAGE_PATTERN =
  /\b(?:root cause|why\b|because\b|defect source|failure mode|cause of|5[-\s]?why|fishbone|hypothesis)\b/i;
const GENERIC_SIPOC_PATTERN =
  /\b(?:quality|operations|management|process|production|team|department|staff|support)\b/i;
const STOP_WORDS = new Set([
  "about", "after", "before", "being", "cause", "causes", "current", "defect", "defects",
  "define", "during", "from", "have", "impact", "into", "line", "loss", "project", "quality",
  "rate", "reduce", "scope", "stage", "states", "step", "steps", "that", "their", "there",
  "these", "this", "through", "using", "with",
]);

function countNonEmpty(items = []) {
  return items.filter((item) => (item || "").trim()).length;
}

function hasRootCauseLanguage(items = []) {
  return items.some((item) => ROOT_CAUSE_LANGUAGE_PATTERN.test(item || ""));
}

function customerDiversityStatus(customers = []) {
  const filledCustomers = customers.filter((item) => (item || "").trim());
  if (filledCustomers.length === 0) {
    return "missing";
  }
  if (filledCustomers.length === 1) {
    return "limited";
  }
  return "good";
}

function normalizeItems(items = []) {
  return items.map((item) => String(item || "").trim());
}

function duplicateValues(items = []) {
  const seen = new Set();
  const duplicates = new Set();
  for (const item of items) {
    const normalized = item.trim().toLowerCase();
    if (!normalized) continue;
    if (seen.has(normalized)) {
      duplicates.add(item.trim());
      continue;
    }
    seen.add(normalized);
  }
  return [...duplicates];
}

function titleForKey(key) {
  const labels = {
    suppliers: "Suppliers",
    inputs: "Inputs",
    process_steps: "Process",
    outputs: "Outputs",
    customers: "Customers",
  };
  return labels[key] || key;
}

function extractKeywords(...values) {
  return [...new Set(values
    .join(" ")
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter((word) => word.length >= 4 && !STOP_WORDS.has(word)))];
}

function scoreFromPenalty(baseScore, penalty, floor = 0) {
  return Math.max(floor, Math.min(100, Math.round(baseScore - penalty)));
}

export function compactSipocEntry(value = "") {
  const normalized = String(value || "")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\.$/, "");
  if (!normalized) return "";

  const shortened = normalized
    .replace(/^the\s+/i, "")
    .replace(/\bproviding\b/gi, "")
    .replace(/\bresponsible for\b/gi, "")
    .replace(/\bused to\b/gi, "")
    .replace(/\bthat is\b/gi, "")
    .replace(/\s{2,}/g, " ")
    .trim();

  if (shortened.length <= 64) {
    return shortened;
  }

  const punctuationSplit = shortened.split(/[;,]|(?:\s+-\s+)/).map((part) => part.trim()).filter(Boolean);
  if (punctuationSplit.length > 0 && punctuationSplit[0].length <= 64) {
    return punctuationSplit[0];
  }

  const words = shortened.split(" ");
  const compactWords = [];
  let length = 0;
  for (const word of words) {
    const nextLength = length ? length + 1 + word.length : word.length;
    if (nextLength > 58) break;
    compactWords.push(word);
    length = nextLength;
  }
  return compactWords.join(" ").trim() || shortened.slice(0, 58).trim();
}

export function normalizeSipocDraftItems(items = []) {
  const compacted = items
    .map((item) => compactSipocEntry(item))
    .filter(Boolean);
  return compacted.length ? compacted : [""];
}

export function validateSipoc(sipocData) {
  const sectionIssues = {};
  const issues = [];
  const sectionStatus = {};
  const keys = ["suppliers", "inputs", "process_steps", "outputs", "customers"];

  for (const key of keys) {
    const label = titleForKey(key);
    const normalizedItems = normalizeItems(sipocData?.[key] || []);
    const filledItems = normalizedItems.filter(Boolean);
    const blanks = normalizedItems.length - filledItems.length;
    const duplicates = duplicateValues(normalizedItems);
    const minimum = SIPOC_MINIMUMS[key];
    const localIssues = [];

    if (filledItems.length < minimum) {
      localIssues.push(`${label} needs at least ${minimum} entries.`);
    }

    if (blanks > 0) {
      localIssues.push(`${label} has ${blanks} empty ${blanks === 1 ? "entry" : "entries"}.`);
    }

    if (duplicates.length > 0) {
      localIssues.push(`${label} contains duplicate values: ${duplicates.join(", ")}.`);
    }

    if (key === "process_steps" && filledItems.length > SIPOC_PROCESS_STEP_THRESHOLD) {
      localIssues.push(`Process must stay between ${SIPOC_MINIMUMS.process_steps} and ${SIPOC_PROCESS_STEP_THRESHOLD} steps.`);
    }

    sectionIssues[key] = localIssues;
    sectionStatus[key] = {
      count: filledItems.length,
      minimum,
      hasIssues: localIssues.length > 0,
    };
    issues.push(...localIssues);
  }

  return {
    isValid: issues.length === 0,
    issues,
    issuesBySection: sectionIssues,
    sectionStatus,
  };
}

export function scoreSipocQuality(sipocData, context = {}) {
  const validation = validateSipoc(sipocData);
  const keys = ["suppliers", "inputs", "process_steps", "outputs", "customers"];
  const normalizedByKey = Object.fromEntries(
    keys.map((key) => [key, normalizeItems(sipocData?.[key] || []).filter(Boolean)])
  );
  const allItems = keys.flatMap((key) => normalizedByKey[key]);
  const multiWordItems = allItems.filter((item) => item.trim().split(/\s+/).length >= 2).length;
  const genericItems = allItems.filter((item) => GENERIC_SIPOC_PATTERN.test(item)).length;
  const rootCauseItems = allItems.filter((item) => ROOT_CAUSE_LANGUAGE_PATTERN.test(item)).length;
  const contextKeywords = extractKeywords(
    context.problemStatement || "",
    context.businessImpact || "",
    context.scope || "",
    context.industry || ""
  );
  const sipocText = allItems.join(" ").toLowerCase();
  const alignedKeywords = contextKeywords.filter((keyword) => sipocText.includes(keyword));
  const processCount = normalizedByKey.process_steps.length;

  let completenessScore = 100;
  for (const key of keys) {
    const count = normalizedByKey[key].length;
    const minimum = SIPOC_MINIMUMS[key];
    if (count < minimum) {
      completenessScore -= Math.min(40, (minimum - count) * 20);
    }
  }
  if (processCount > SIPOC_PROCESS_STEP_THRESHOLD) {
    completenessScore -= (processCount - SIPOC_PROCESS_STEP_THRESHOLD) * 10;
  }
  if (!validation.isValid) {
    completenessScore = Math.min(completenessScore, 45);
  }
  completenessScore = Math.max(0, Math.min(100, completenessScore));

  const clarityPenalty =
    genericItems * 10 +
    rootCauseItems * 15 +
    Math.max(0, allItems.length - multiWordItems) * 3;
  const clarityScore = scoreFromPenalty(100, clarityPenalty, 20);

  let alignmentScore = 65;
  if (contextKeywords.length > 0) {
    const coverage = alignedKeywords.length / contextKeywords.length;
    if (coverage >= 0.45) alignmentScore = 95;
    else if (coverage >= 0.25) alignmentScore = 82;
    else if (coverage >= 0.12) alignmentScore = 68;
    else alignmentScore = 45;
  }
  if (genericItems > 0) {
    alignmentScore = Math.max(25, alignmentScore - genericItems * 5);
  }

  const averageLength = allItems.length
    ? allItems.reduce((sum, item) => sum + item.length, 0) / allItems.length
    : 0;
  let detailScore = 30;
  if (processCount >= 3 && processCount <= SIPOC_PROCESS_STEP_THRESHOLD) {
    detailScore = 75;
  }
  if (processCount >= 5 && processCount <= SIPOC_PROCESS_STEP_THRESHOLD) {
    detailScore += 10;
  }
  if (averageLength >= 16) {
    detailScore += 10;
  } else if (averageLength < 10) {
    detailScore -= 15;
  }
  if (genericItems > 0) {
    detailScore -= genericItems * 4;
  }
  detailScore = Math.max(20, Math.min(100, Math.round(detailScore)));

  const score = Math.round(
    completenessScore * 0.35 +
    clarityScore * 0.2 +
    alignmentScore * 0.25 +
    detailScore * 0.2
  );

  const feedback = [];
  if (!validation.isValid) {
    feedback.push("Meet the SIPOC minimums first: 3 suppliers, 3 inputs, 3 to 7 process steps, 2 outputs, and 2 customers with no blanks or duplicates.");
  }
  if (clarityScore < 70) {
    feedback.push("Replace generic names with real roles or sources, such as named suppliers, specific work centers, or actual inspection points.");
  }
  if (alignmentScore < 70) {
    feedback.push("Tie suppliers, outputs, and customers directly to the defect problem and in-scope flow instead of broad department labels.");
  }
  if (detailScore < 70) {
    feedback.push("Use short operational phrases, not sentence-length descriptions, so each SIPOC item is easy to scan quickly.");
  }
  if (feedback.length === 0) {
    feedback.push("SIPOC quality is strong enough for Define gate review. Confirm the items remain aligned as the problem statement evolves.");
  }

  return {
    score,
    feedback,
    categories: {
      completeness: completenessScore,
      clarity: clarityScore,
      alignment_to_problem: alignmentScore,
      level_of_detail: detailScore,
    },
    isValid: validation.isValid && score >= SIPOC_QUALITY_VALID_THRESHOLD,
  };
}

export function getSipocGuardrails(sipoc) {
  const warnings = [];
  const processStepCount = countNonEmpty(sipoc?.process_steps);
  const validation = validateSipoc(sipoc);

  if (processStepCount > SIPOC_PROCESS_STEP_THRESHOLD) {
    warnings.push({
      id: "process-detail",
      message: `This SIPOC has ${processStepCount} process steps. Keep it to about ${SIPOC_PROCESS_STEP_THRESHOLD} high-level steps so it stays a scoping tool instead of a detailed process map.`,
    });
  }

  const fieldsToCheck = [
    ...(sipoc?.suppliers || []),
    ...(sipoc?.inputs || []),
    ...(sipoc?.process_steps || []),
    ...(sipoc?.outputs || []),
    ...(sipoc?.customers || []),
  ];
  if (hasRootCauseLanguage(fieldsToCheck)) {
    warnings.push({
      id: "root-cause-language",
      message: "SIPOC fields should describe the high-level flow, not root causes or hypotheses. Move cause analysis to Fishbone or Analyze work.",
    });
  }

  const customerStatus = customerDiversityStatus(sipoc?.customers);
  if (customerStatus === "missing") {
    warnings.push({
      id: "customer-coverage",
      message: "Add customers to show who receives the output. Include internal and external customers where they matter.",
    });
  } else if (customerStatus === "limited") {
    warnings.push({
      id: "customer-coverage",
      message: "Consider whether both internal and external customers should appear here. SIPOC works best when downstream recipients are visible.",
    });
  }

  if (!validation.isValid) {
    warnings.push({
      id: "validation",
      message: "SIPOC incomplete or insufficient detail. Fill each column to the minimum standard and remove blank or duplicate entries before gate review.",
    });
  }

  return { warnings, processStepCount, validation };
}
